/**
 * Reusable confirmation popover anchored to a trigger element (dropdown-style),
 * a nicer replacement for window.confirm.
 *
 * Self-contained: injects its own styles once and resolves a Promise<boolean>.
 * Text is German (with per-call overrides); no translation layer.
 *
 * @param {HTMLElement} anchorEl - element the popover points at.
 * @param {Object} [options]
 * @param {string} [options.title] - heading text.
 * @param {string} [options.message] - body text (overrides the name template).
 * @param {string} [options.name] - interpolated into the default "{name} …" message.
 * @param {string} [options.confirmLabel]
 * @param {string} [options.cancelLabel]
 * @param {'danger'|'default'} [options.tone='danger'] - confirm button style.
 * @param {AbortSignal} [options.signal] - dismiss when the caller is disposed.
 * @returns {Promise<boolean>} resolves true on confirm, false otherwise.
 */
const CONFIRM_POPOVER_TEXT = {
  title: 'Löschen bestätigen',
  message: 'Dieser Eintrag wird dauerhaft entfernt. Diese Aktion kann nicht rückgängig gemacht werden.',
  messageWithName: '„{name}“ wird dauerhaft entfernt. Diese Aktion kann nicht rückgängig gemacht werden.',
  confirm: 'Löschen',
  cancel: 'Abbrechen',
}

function ensureConfirmPopoverStyles() {
  if (document.getElementById('lux-confirm-popover-style')) return
  const style = document.createElement('style')
  style.id = 'lux-confirm-popover-style'
  style.textContent = `
    .lux-confirm-pop { position:fixed; z-index:10001; width:250px; background:#fff; border:1px solid #f0f0f0; border-radius:8px; box-shadow:0 6px 24px rgba(0,0,0,.16); padding:14px; font-family:'IBM Plex Sans',sans-serif; animation:lux-confirm-in .12s ease-out; }
    @keyframes lux-confirm-in { from { opacity:0; transform:translateY(-4px); } to { opacity:1; transform:translateY(0); } }
    .lux-confirm-title { display:flex; align-items:center; gap:8px; margin:0 0 6px; font-size:13px; font-weight:600; color:rgba(0,0,0,.88); }
    .lux-confirm-title svg { flex:none; color:#faad14; }
    .lux-confirm-text { margin:0 0 14px; font-size:12px; line-height:1.5; color:rgba(0,0,0,.55); word-break:break-word; }
    .lux-confirm-actions { display:flex; justify-content:flex-end; gap:8px; }
    .lux-confirm-btn { height:30px; padding:0 14px; border-radius:6px; font-size:12px; font-weight:500; cursor:pointer; border:1px solid #d9d9d9; background:#fff; color:rgba(0,0,0,.85); transition:background .15s,border-color .15s; }
    .lux-confirm-btn:hover { background:#fafafa; border-color:#bfbfbf; }
    .lux-confirm-btn-danger { background:#ff4d4f; border-color:#ff4d4f; color:#fff; }
    .lux-confirm-btn-danger:hover { background:#d9363e; border-color:#d9363e; }
    .lux-confirm-btn-primary { background:#000026; border-color:#000026; color:#fff; }
    .lux-confirm-btn-primary:hover { background:#1a1a3d; border-color:#1a1a3d; }
    .lux-confirm-arrow { position:absolute; width:10px; height:10px; background:#fff; transform:rotate(45deg); }
  `
  document.head.appendChild(style)
}

const confirmPopover = (anchorEl, options = {}) =>
  new Promise((resolve) => {
    ensureConfirmPopoverStyles()
    document.querySelectorAll('.lux-confirm-pop').forEach((el) => el.remove())

    const dict = CONFIRM_POPOVER_TEXT
    const tone = options.tone === 'default' ? 'default' : 'danger'
    const title = options.title ?? dict.title
    const cancelLabel = options.cancelLabel ?? dict.cancel
    const confirmLabel = options.confirmLabel ?? dict.confirm
    const message =
      options.message ??
      (options.name != null && options.name !== ''
        ? dict.messageWithName.replace('{name}', String(options.name))
        : dict.message)

    const pop = document.createElement('div')
    pop.className = 'lux-confirm-pop'
    pop.setAttribute('role', 'dialog')
    pop.setAttribute('aria-modal', 'true')

    const titleEl = document.createElement('p')
    titleEl.className = 'lux-confirm-title'
    titleEl.innerHTML =
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>'
    const titleText = document.createElement('span')
    titleText.textContent = title
    titleEl.appendChild(titleText)

    const textEl = document.createElement('p')
    textEl.className = 'lux-confirm-text'
    textEl.textContent = message

    const actions = document.createElement('div')
    actions.className = 'lux-confirm-actions'
    const cancelBtn = document.createElement('button')
    cancelBtn.type = 'button'
    cancelBtn.className = 'lux-confirm-btn lux-confirm-cancel'
    cancelBtn.textContent = cancelLabel
    const confirmBtn = document.createElement('button')
    confirmBtn.type = 'button'
    confirmBtn.className = `lux-confirm-btn ${tone === 'danger' ? 'lux-confirm-btn-danger' : 'lux-confirm-btn-primary'} lux-confirm-ok`
    confirmBtn.textContent = confirmLabel
    actions.appendChild(cancelBtn)
    actions.appendChild(confirmBtn)

    const arrow = document.createElement('span')
    arrow.className = 'lux-confirm-arrow'

    pop.appendChild(titleEl)
    pop.appendChild(textEl)
    pop.appendChild(actions)
    pop.appendChild(arrow)
    pop.style.visibility = 'hidden'
    document.body.appendChild(pop)

    const position = () => {
      const rect = anchorEl.getBoundingClientRect()
      const width = pop.offsetWidth
      const height = pop.offsetHeight
      const gap = 10
      let left = rect.right - width
      left = Math.min(Math.max(left, 8), window.innerWidth - width - 8)
      let top = rect.bottom + gap
      let below = true
      if (top + height > window.innerHeight - 8) {
        top = rect.top - gap - height
        below = false
      }
      pop.style.left = `${Math.round(left)}px`
      pop.style.top = `${Math.round(top)}px`
      const arrowLeft = Math.min(Math.max(rect.left + rect.width / 2 - left - 5, 12), width - 22)
      arrow.style.left = `${Math.round(arrowLeft)}px`
      if (below) {
        arrow.style.top = '-5px'
        arrow.style.borderLeft = '1px solid #f0f0f0'
        arrow.style.borderTop = '1px solid #f0f0f0'
        arrow.style.borderRight = 'none'
        arrow.style.borderBottom = 'none'
      } else {
        arrow.style.top = `${height - 5}px`
        arrow.style.borderRight = '1px solid #f0f0f0'
        arrow.style.borderBottom = '1px solid #f0f0f0'
        arrow.style.borderLeft = 'none'
        arrow.style.borderTop = 'none'
      }
      pop.style.visibility = 'visible'
    }
    position()

    let listenerTimer = null
    let settled = false
    let cleanup = () => {}
    const done = (result) => {
      if (settled) return
      settled = true
      cleanup()
      resolve(result)
    }
    const onDocPointer = (event) => {
      if (!pop.contains(event.target) && event.target !== anchorEl) done(false)
    }
    const onKey = (event) => {
      if (event.key === 'Escape') done(false)
    }
    const onAbort = () => done(false)
    cleanup = () => {
      clearTimeout(listenerTimer)
      options.signal?.removeEventListener('abort', onAbort)
      document.removeEventListener('mousedown', onDocPointer, true)
      document.removeEventListener('keydown', onKey, true)
      window.removeEventListener('resize', position, true)
      window.removeEventListener('scroll', position, true)
      pop.remove()
    }
    cancelBtn.addEventListener('click', () => done(false))
    confirmBtn.addEventListener('click', () => done(true))
    options.signal?.addEventListener('abort', onAbort, { once: true })
    if (options.signal?.aborted) return done(false)
    // Defer global listeners so the originating click doesn't immediately dismiss the popover.
    listenerTimer = setTimeout(() => {
      if (settled) return
      document.addEventListener('mousedown', onDocPointer, true)
      document.addEventListener('keydown', onKey, true)
      window.addEventListener('resize', position, true)
      window.addEventListener('scroll', position, true)
    }, 0)
  })
